// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory
import fetch from 'cross-fetch';
import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const handleRequest = (url, options) => {
  return fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// const get = (path, params = {}, headers) => {
//   const url = `${apiBaseUrl()}${path}`;
//   const queryParams = new URLSearchParams(params).toString();
//   const options = {
//     method: 'GET',
//     credentials: 'include',
//     headers: {
//       'Content-Type': 'application/transit+json',
//       ...headers,
//     },
//   };
//   return handleRequest(`${url}${queryParams ? '?' : ''}${queryParams}`, options);
// };

const get = (path, params = {}, headers) => {
  const url = `${apiBaseUrl()}${path}`;
  const queryParams = new URLSearchParams(params).toString();
  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
      ...headers,
    },
  };
  return handleRequest(`${url}${queryParams ? '?' : ''}${queryParams}`, options);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const fetchBlogs = body => {
  return post('/api/fetch-blogs', body);
};
export const fetchTagsFilter = body => {
  return post('/api/fetch-tags-filter', body);
};

export const fetchBlog = body => {
  return post('/api/fetch-blog', body);
};

export const fetchTutorials = body => {
  return post('/api/cms/fetch-tutorials', body);
};

export const fetchAvailabilties = body => {
  return post('/api/fetch-availabilties', body);
};

export const subscribeMailChimpNewsLetter = body => {
  return post('/api/mailchimp/subscribe', body);
};

export const getGoogleAccessToken = body => {
  return post('/api/google/access-token', body);
};

export const exchangeCode = body => {
  return post('/api/google/exchange-code', body);
};

export const getGoogleCalendarAndEvents = body => {
  return post('/api/google/fetch-calendar-and-events', body);
};

export const createGoogleCalendarEvent = body => {
  return post('/api/google/create-event', body);
};

export const verifyCoupon = body => {
  return post('/api/stripe/verify-coupon', body);
};

export const sendSendGridNotification = body => {
  return post('/api/sendgrid/send-notification', body);
};

export const createSendgridContact = body => {
  return post('/api/sendgrid/create-contact', body);
};

export const updateReceiptParamsInTransaction = body => {
  return post('/api/integration/update-receipt', body);
};

export const updateMeetingLink = body => {
  return post('/api/integration/update-meeting-link', body);
};

export const createJitsiJWT = body => {
  return post('/api/create-meeting-jwt', body);
};

export const showStripeAccount = body => {
  return post('/api/show-stripe-account', body);
};

export const deleteCoupon = body => {
  return post('/api/delete-coupon', body);
};

export const uploadObject = body => {
  return post('/api/upload-object', body);
};

//Dashboard API Calls
export const queryUsersData = body => {
  return post('/api/data/query-users', body);
};

export const queryTransactionsData = body => {
  return post('/api/data/query-transactions', body);
};

export const settleProvidePayout = body => {
  return post('/api/settle-provider-payout', body);
};
export const getSubscriptionProductPrice = body => {
  return get('/api/stripe/get-product-price', body);
};

export const createStripeSubscription = body => {
  return post('/api/stripe/create-subscription', body);
};

export const getEvents = body => {
  return post('/api/cms/get-events', body);
};

export const getEventTags = body => {
  return post('/api/cms/get-event-tags', body);
};

export const getEvent = body => {
  return post('/api/cms/get-event', body);
};

export const getTopics = body => {
  return post('/api/cms/get-topics', body);
};

export const getTheBasics = body => {
  return post('/api/cms/get-the-basics', body);
};

export const getTutorialSubcategories = body => {
  return post('/api/cms/get-tutorial-subcategories', body);
};

export const getPopularItems = body => {
  return post('/api/cms/get-popular-items', body);
};

export const getTopicDetails = body => {
  return post('/api/cms/get-topic-details', body);
};

export const getSymptoms = body => {
  return post('/api/cms/get-symptoms', body);
};

export const getSymptomsCategories = body => {
  return post('/api/cms/get-symptoms-categories', body);
};

export const getProducts = body => {
  return post('/api/cms/get-products', body);
};

export const getProductStages = body => {
  return post('/api/cms/get-product-stages', body);
};

export const getProductTypes = body => {
  return post('/api/cms/get-product-types', body);
};


export const verifyVoucherCode = body => {
  return post('/api/verify-voucher-code', body);
};

export const search = body => {
  return post('/api/milvus/search', body);
};

export const getStripeCheckoutSessionPayment = body => {
  const { sessionId } = body;
  return get(`/api/stripe/retrieve-checkout-session`, { sessionId });
};

export const createStripeCheckoutSession = body => {
  return post('/api/stripe/create-checkout-session', body);
};

export const confirmStripeCheckoutSession = body => {
  return post(`/api/stripe/confirm-stripe-checkout-session`, body);
};

export const cancelSubscriptionPlan = body => {
  return post(`/api/stripe/cancel-subscription-plan`, body);
};
export const createStripeSignupSubscription = body => {
  return post(`/api/stripe/create-signup-subscription`, body);
};

export const streamAIChat = body => {
  return post(`/api/gemma/chat-stream`, body);
};
